'use client'

import { useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { Check, ArrowRight, Loader2, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useCheckout } from '@/hooks/use-checkout'
import type { PlanTier } from '@/lib/billing'
import type { MarketingPlan } from '@/config/pricing'
import { ComparisonTableFull } from './pricing-comparison'
import { usePricing } from '@/context/pricing-context'

function PricingCard({
  plan,
  isYearly,
  index,
  onCheckout,
  isLoading,
  loadingPlan,
  isRecommended,
}: {
  plan: MarketingPlan
  isYearly: boolean
  index: number
  onCheckout: (plan: PlanTier) => void
  isLoading: boolean
  loadingPlan: PlanTier | null
  isRecommended: boolean
}) {
  const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice
  const monthlyPriceOriginal = plan.monthlyPrice
  const isPlanLoading = isLoading && loadingPlan === plan.planTier

  const isHighlighted = isRecommended || (!isRecommended && plan.popular)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.4, delay: index * 0.1, ease: 'easeOut' }}
      className="relative h-full"
    >
      <Card
        className={cn(
          'flex h-full flex-col overflow-hidden transition-[transform,box-shadow,border-color,background-color,color] duration-300 motion-reduce:transition-none',
          isHighlighted
            ? 'border-black/10 shadow-2xl shadow-black/5 ring-1 ring-black/5 lg:scale-105 dark:border-white/10 dark:shadow-white/5 dark:ring-white/10'
            : 'border-transparent bg-gray-50/50 hover:bg-white hover:shadow-lg dark:bg-white/5 dark:hover:bg-white/10'
        )}
      >
        {isRecommended && (
          <div className="absolute left-0 right-0 top-0 flex justify-center pt-4">
            <Badge
              variant="secondary"
              className="bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 dark:text-emerald-400"
            >
              <Sparkles className="mr-1 h-3 w-3" aria-hidden="true" /> Recomendado para ti
            </Badge>
          </div>
        )}

        {!isRecommended && plan.popular && (
          <div className="absolute right-6 top-6">
            <span className="bg-foreground block h-2 w-2 rounded-full" />
          </div>
        )}

        <CardHeader className={cn('pb-8', isRecommended ? 'pt-12' : 'pt-8')}>
          <CardTitle className="text-xl">{plan.name}</CardTitle>
          <CardDescription className="text-[15px] leading-relaxed">
            {plan.description}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-1 flex-col gap-8">
          <div className="flex flex-col">
            <div className="flex items-baseline gap-1">
              {price !== null ? (
                <>
                  <span className="text-foreground text-4xl font-bold tracking-tighter">
                    ${price}
                  </span>
                  <span className="text-muted-foreground text-sm font-medium">/mes</span>
                </>
              ) : (
                <span className="text-foreground text-3xl font-bold tracking-tight">A medida</span>
              )}
            </div>

            {isYearly && price !== null && monthlyPriceOriginal && (
              <span className="mt-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                Ahorras ${(monthlyPriceOriginal - price) * 12} al año
              </span>
            )}
          </div>

          <div className="bg-border h-px w-full" />

          <ul className="space-y-3">
            {plan.features.map((feature) => (
              <li key={feature} className="flex items-start gap-3">
                <Check
                  className="text-foreground mt-0.5 h-4 w-4 shrink-0"
                  strokeWidth={2.5}
                  aria-hidden="true"
                />
                <span className="text-muted-foreground text-[14px] font-medium">{feature}</span>
              </li>
            ))}
          </ul>
        </CardContent>

        <CardFooter className="pt-0">
          {plan.isContactSales ? (
            <Button
              asChild
              className={cn(
                'h-12 w-full rounded-full text-[15px] font-semibold transition-[transform,box-shadow,background-color,border-color,color,opacity] motion-reduce:transition-none',
                isHighlighted
                  ? 'bg-foreground text-background hover:bg-foreground/90'
                  : 'bg-background text-foreground border-input hover:bg-accent hover:text-accent-foreground border'
              )}
            >
              <Link href="/contacto">
                {plan.cta} <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
          ) : (
            <Button
              onClick={() => onCheckout(plan.planTier)}
              disabled={isLoading}
              className={cn(
                'h-12 w-full rounded-full text-[15px] font-semibold transition-[transform,box-shadow,background-color,border-color,color,opacity] motion-reduce:transition-none',
                isHighlighted
                  ? 'bg-foreground text-background hover:bg-foreground/90'
                  : 'bg-background text-foreground border-input hover:bg-accent hover:text-accent-foreground border'
              )}
            >
              {isPlanLoading ? (
                <>
                  <Loader2
                    className="h-4 w-4 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                  <span className="sr-only">{`Procesando ${plan.cta}`}</span>
                </>
              ) : (
                <>
                  {plan.cta} <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                </>
              )}
            </Button>
          )}
        </CardFooter>
      </Card>
    </motion.div>
  )
}

export function PricingSectionInteractive({ plans }: { plans: MarketingPlan[] }) {
  const context = usePricing()
  const isYearly = context.billingPeriod === 'yearly'
  const togglePeriod = (yearly: boolean) => context.setBillingPeriod(yearly ? 'yearly' : 'monthly')

  const [showComparison, setShowComparison] = useState(false)
  const { checkout, isLoading, loadingPlan } = useCheckout()

  const handleCheckout = (plan: PlanTier) =>
    checkout(plan, {
      billingPeriod: context.billingPeriod,
    })

  return (
    <div className="relative">
      {/* Minimal Toggle */}
      <div className="mx-auto mb-16 flex justify-center">
        <div className="border-input bg-background relative flex items-center rounded-full border p-1 shadow-sm">
          <button
            type="button"
            onClick={() => togglePeriod(false)}
            className={cn(
              'relative z-10 rounded-full px-6 py-2 text-sm font-medium transition-colors duration-200',
              !isYearly ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {!isYearly && (
              <motion.div
                layoutId="active-pill-pricing"
                className="bg-muted absolute inset-0 rounded-full"
                transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
              />
            )}
            <span className="relative z-10">Mensual</span>
          </button>
          <button
            type="button"
            onClick={() => togglePeriod(true)}
            className={cn(
              'relative z-10 flex items-center gap-2 rounded-full px-6 py-2 text-sm font-medium transition-colors duration-200',
              isYearly ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {isYearly && (
              <motion.div
                layoutId="active-pill-pricing"
                className="bg-muted absolute inset-0 rounded-full"
                transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
              />
            )}
            <span className="relative z-10">Anual</span>
            <Badge
              variant="secondary"
              className={cn(
                'relative z-10 ml-1.5 px-1.5 py-0 text-[10px] font-bold uppercase tracking-wide',
                isYearly
                  ? 'bg-background shadow-sm'
                  : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400'
              )}
            >
              -20%
            </Badge>
          </button>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
        {plans.map((plan, index) => (
          <PricingCard
            key={plan.name}
            plan={plan}
            isYearly={isYearly}
            index={index}
            onCheckout={handleCheckout}
            isLoading={isLoading}
            loadingPlan={loadingPlan}
            isRecommended={context.recommendedPlan === plan.planTier}
          />
        ))}
      </div>

      {/* Comparison Toggle - Cleaner */}
      <div className="border-border mx-auto mt-24 max-w-5xl border-t pt-12 text-center">
        <Button
          variant="ghost"
          onClick={() => setShowComparison(!showComparison)}
          className="text-muted-foreground hover:text-foreground group"
        >
          {showComparison ? 'Ocultar comparativa completa' : 'Comparar características detalladas'}
          <ArrowRight
            className={cn(
              'ml-2 h-4 w-4 transition-transform duration-200',
              showComparison ? '-rotate-90' : 'rotate-90'
            )}
          />
        </Button>

        <AnimatePresence>
          {showComparison && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="mt-12 overflow-hidden"
            >
              <ComparisonTableFull plans={plans} isYearly={isYearly} onCheckout={handleCheckout} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
